import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Loader2,
  X,
  Tag,
  Plus,
  Upload,
  File,
  Download,
  Trash2,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Calendar,
  FileCheck,
  FileX,
  FileQuestion,
} from "lucide-react";
import { Document, DocumentGroup, DocumentAttachment } from "@/models";
import {
  documentGroupApi,
  documentTypeApi,
  enhancedDocumentApi as documentApi,
} from "@/services/api";
import { cmsApi } from "@/services/api/cmsApi";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import FormBuilder from "./FormBuilder/FormBuilder";
import DocumentFormBuilder from "./DocumentFormBuilder";
import CompactFileUploader from "@/components/shared/CompactFileUploader";
import { newUploadTarget, removeStorageObject } from "@/utils/storageRefs";
import { supabase } from "@/integrations/supabase/client";
import {
  calculateExpirationDate,
  formatExpirationSettings,
} from "@/utils/expirationCalculator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  document_type_id: z.coerce.number({
    required_error: "Document type is required",
  }),
  publicly_available: z.boolean().default(true),
  // Output template override (8-Sep): null = inherit the document type's
  // template, which is how every existing document behaves.
  template_id: z.coerce.number().nullish(),
  document_groups: z.array(z.number()).optional(),
  expiration_override_type: z.enum(["days", "annual", "date"]).optional(),
  expiration_override_days: z.coerce.number().nullish(),
  expiration_override_day: z.coerce.number().min(1).max(31).nullish(),
  expiration_override_month: z.coerce.number().min(1).max(12).nullish(),
  expiration_override_year: z.coerce.number().min(2024).nullish(),
});

type FormValues = z.infer<typeof formSchema>;

interface DocumentFormProps {
  documentProp?: Document;
  onSuccess?: (documentId?: number) => void;
  readOnly?: boolean;
  submitButtonText?: string;
  formId?: string;
  dialogFormSubmitDisabled?: boolean;
  preventFormSubmission?: boolean;
}

const badgeColors = [
  "default",
  "secondary",
  "destructive",
  "outline",
  "success",
  "warning",
];

const DocumentForm = ({
  documentProp,
  onSuccess,
  readOnly = false,
  submitButtonText,
  formId = "document-form",
  dialogFormSubmitDisabled = false,
  preventFormSubmission = false,
}: DocumentFormProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedRole, user } = useAuth();
  const [newGroupName, setNewGroupName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState<DocumentAttachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [expirationDialogOpen, setExpirationDialogOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] =
    useState<DocumentAttachment | null>(null);
  const [expirationDate, setExpirationDate] = useState("");
  const [effectiveDateDialogOpen, setEffectiveDateDialogOpen] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState("");

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: documentProp?.name || "",
      document_type_id: documentProp?.document_type_id || 0,
      publicly_available: documentProp?.publicly_available ?? true,
      template_id: (documentProp as any)?.template_id ?? null,
      document_groups: documentProp?.document_groups || [],
      expiration_override_type:
        documentProp?.expiration_override_type || undefined,
      expiration_override_days: documentProp?.expiration_override_days || null,
      expiration_override_day: documentProp?.expiration_override_day || null,
      expiration_override_month:
        documentProp?.expiration_override_month || null,
      expiration_override_year: documentProp?.expiration_override_year || null,
    },
  });

  const { data: templateFolders = [] } = useQuery({
    queryKey: ["templateFolders"],
    queryFn: () => cmsApi.getTemplateFolders(),
  });

  // Only offer templates compatible with this document's type — plus generic
  // folders and whatever is currently selected — within the caller's scope
  // (8-Sep: "¿cómo evitás que elija un template que no tiene nada que ver?").
  const watchedDocTypeId = form.watch("document_type_id");
  const watchedTemplateId = form.watch("template_id");
  const compatibleTemplateFolders = (templateFolders as any[]).filter((f) => {
    const scopeOk =
      selectedRole?.type === "management_company"
        ? f.management_company_id == null ||
          Number(f.management_company_id) === Number(selectedRole.entityId)
        : true;
    const compatOk =
      f.document_type_id == null ||
      (watchedDocTypeId &&
        Number(f.document_type_id) === Number(watchedDocTypeId)) ||
      (watchedTemplateId != null && Number(f.id) === Number(watchedTemplateId));
    return scopeOk && compatOk;
  });


  const { data: documentTypes = [], isLoading: loadingTypes } = useQuery({
    queryKey: ["documentTypes", {}],
    queryFn: documentTypeApi.list,
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading document types",
          description: error.message || "Failed to fetch document types",
        });
      },
    },
  });

  const {
    data: documentGroups = [],
    isLoading: loadingGroups,
    refetch: refetchGroups,
  } = useQuery({
    queryKey: ["documentGroups"],
    queryFn: () => {
      // Backend now handles role-based filtering automatically through permissions
      return documentGroupApi.list();
    },
    enabled: !!selectedRole,
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading document groups",
          description: error.message || "Failed to fetch document groups",
        });
      },
    },
  });

  const safeDocumentGroups = Array.isArray(documentGroups)
    ? documentGroups
    : [];
  const safeDocumentTypes = Array.isArray(documentTypes) ? documentTypes : [];

  // Get the selected document type to determine source
  const selectedDocumentTypeId = form.watch("document_type_id");
  const selectedDocumentType = safeDocumentTypes.find(
    (type) => type.id === selectedDocumentTypeId
  );
  const sourceType =
    selectedDocumentType?.source === "file" ? "File" : "Platform";

  const currentDocumentGroups = form.watch("document_groups") || [];

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;

    try {
      await documentGroupApi.create({
        name: newGroupName.trim(),
        description: `Group created for ${newGroupName.trim()} documents`,
        // Backend will automatically set management_company_id based on selected role
      });

      toast({
        title: "Document group created",
        description: `"${newGroupName}" has been created successfully.`,
      });

      setNewGroupName("");
      await refetchGroups();

      const newGroups = await documentGroupApi.list();
      const newGroup = Array.isArray(newGroups)
        ? newGroups.find((g) => g.name === newGroupName.trim())
        : null;

      if (newGroup) {
        const currentGroups = form.getValues("document_groups") || [];
        form.setValue("document_groups", [...currentGroups, newGroup.id]);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error creating document group",
        description: error.message || "Failed to create document group",
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && newGroupName.trim()) {
      e.preventDefault();
      handleCreateGroup();
    }
  };

  const onSubmit = async (data: FormValues) => {
    try {
      if (documentProp) {
        await documentApi.update(documentProp.id, data);
        toast({
          title: "Document updated",
          description: "The document has been updated successfully.",
        });
        if (onSuccess) {
          onSuccess(documentProp.id);
        } else {
          navigate("/backoffice/documents");
        }
      } else {
        const newDocumentData = {
          name: data.name,
          document_type_id: data.document_type_id,
          publicly_available: data.publicly_available,
          template_id: data.template_id ?? null,
          document_groups: data.document_groups,
          expiration_override_type: data.expiration_override_type,
          expiration_override_days: data.expiration_override_days,
          expiration_override_day: data.expiration_override_day,
          expiration_override_month: data.expiration_override_month,
          expiration_override_year: data.expiration_override_year,
        };
        const newDocument = await documentApi.create(newDocumentData);
        toast({
          title: "Document created",
          description: "The document has been created successfully.",
        });
        if (onSuccess) {
          onSuccess(newDocument.id);
        } else {
          navigate("/backoffice/documents");
        }
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error saving document",
        description: error.message || "There was an error saving the document.",
      });
    }
  };

  const isLoading =
    form.formState.isSubmitting || loadingTypes || loadingGroups;

  const groupsById = safeDocumentGroups.reduce((acc, group) => {
    acc[group.id] = group.name || `Group ${group.id}`;
    return acc;
  }, {} as Record<number, string>);

  const addGroupToSelection = (groupId: number) => {
    const currentGroups = form.getValues("document_groups") || [];
    if (!currentGroups.includes(groupId)) {
      form.setValue("document_groups", [...currentGroups, groupId]);
    }
  };

  const removeGroupFromSelection = (groupId: number) => {
    const currentGroups = form.getValues("document_groups") || [];
    form.setValue(
      "document_groups",
      currentGroups.filter((id) => id !== groupId)
    );
  };

  const [filteredSuggestions, setFilteredSuggestions] = useState<
    DocumentGroup[]
  >([]);

  useEffect(() => {
    if (newGroupName.trim()) {
      const filtered = safeDocumentGroups.filter((group) => {
        const groupName = group.name || `Group ${group.id}`;
        return (
          groupName.toLowerCase().includes(newGroupName.toLowerCase()) &&
          !currentDocumentGroups.includes(group.id)
        );
      });
      setFilteredSuggestions(filtered);
    } else {
      setFilteredSuggestions([]);
    }
  }, [newGroupName, safeDocumentGroups, currentDocumentGroups]);

  // Fetch existing attachments when document loads
  useEffect(() => {
    const fetchAttachments = async () => {
      if (documentProp?.id && sourceType === "File") {
        setLoadingAttachments(true);
        try {
          const attachmentData = await documentApi.listAttachments(
            documentProp.id
          );
          setAttachments((attachmentData as DocumentAttachment[]) || []);
        } catch (error) {
          console.error("Error fetching attachments:", error);
          toast({
            variant: "destructive",
            title: "Error loading attachments",
            description: "Failed to load existing files.",
          });
        } finally {
          setLoadingAttachments(false);
        }
      }
    };

    fetchAttachments();
  }, [documentProp?.id, sourceType, toast]);

  const handleFileUpload = async (file: File) => {
    if (!documentProp?.id) {
      toast({
        variant: "destructive",
        title: "Document not saved",
        description: "Please save the document before uploading files.",
      });
      return;
    }

    setUploading(true);
    try {
      // Private bucket, non-guessable name; the row stores the canonical
      // storage:// ref, opened later through storage-url.
      const { path: filePath, ref: fileRef } = newUploadTarget(
        "document_files",
        `document_${documentProp.id}`,
        file
      );

      // Upload to document_files bucket
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("document_files")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        throw uploadError;
      }

      console.log("Upload successful:", uploadData?.path);

      // Calculate expiration date based on document type settings
      const selectedDocumentType = safeDocumentTypes.find(
        (type) => type.id === selectedDocumentTypeId
      );
      const expirationDate = selectedDocumentType
        ? calculateExpirationDate(selectedDocumentType)
        : null;

      // Check if there's already a current file for this document
      const hasCurrentFile = attachments.some(
        (attachment) => attachment.status === "current"
      );
      const fileStatus = hasCurrentFile ? "upcoming" : "current";

      console.log("File status decision:", { hasCurrentFile, fileStatus });

      // Create attachment record in database
      const attachmentResponse = await documentApi.createAttachment(
        documentProp.id,
        {
          file_path: fileRef,
          file_name: file.name,
          file_size: file.size,
          mime_type: file.type,
          status: fileStatus,
          ...(expirationDate && { expires_at: expirationDate.toISOString() }),
        }
      );

      console.log("Attachment response received:", attachmentResponse);

      // Handle response - Supabase returns an array even for single inserts
      const attachmentData = Array.isArray(attachmentResponse)
        ? attachmentResponse[0]
        : attachmentResponse;

      // Add to local state
      setAttachments((prev) => [attachmentData as DocumentAttachment, ...prev]);

      toast({
        title: "File uploaded",
        description: hasCurrentFile
          ? `${file.name} uploaded as upcoming file`
          : `${file.name} uploaded as current file`,
      });

      // Trigger parent refresh to update document status
      // Uploading files may change the overall document status due to database triggers
      if (onSuccess && documentProp?.id) {
        onSuccess(documentProp.id);
      }
    } catch (err: any) {
      console.error("Complete upload error:", err);

      // Check if it's a bucket not found error
      if (err.message?.includes("document_files")) {
        toast({
          variant: "destructive",
          title: "Storage bucket not configured",
          description:
            "The document_files storage bucket needs to be created. Please contact your administrator.",
        });
      } else {
        const currentFileCount = attachments.filter(att => att.status === "current").length;
        const upcomingFileCount = attachments.filter(att => att.status === "upcoming").length;
        
        toast({
          variant: "destructive",
          title: "Upload failed",
          description: `Failed to upload file. Current files: ${currentFileCount}, Upcoming files: ${upcomingFileCount}. Error: ${err.message || "Unknown error"}`,
        });
      }
    }
    setUploading(false);
  };

  const handleDeleteAttachment = async (attachment: DocumentAttachment) => {
    try {
      // Delete from database
      await documentApi.deleteAttachment(attachment.id);

      // Delete the object too: canonical ref, public URL or bare path.
      await removeStorageObject(attachment.file_path, "document_files");

      // Remove from local state
      setAttachments((prev) => prev.filter((a) => a.id !== attachment.id));

      toast({
        title: "File deleted",
        description: attachment.file_name || "File removed successfully",
      });

      // Trigger parent refresh to update document status
      // Deleting attachments may change the overall document status due to database triggers
      if (onSuccess && documentProp?.id) {
        onSuccess(documentProp.id);
      }
    } catch (error: any) {
      console.error("Error deleting attachment:", error);
      
      const statusLabels = {
        current: "Current",
        upcoming: "Upcoming", 
        expired: "Expired",
        disabled: "Disabled"
      };
      
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: `Failed to delete ${statusLabels[attachment.status] || attachment.status} file "${attachment.file_name || 'Unknown file'}". ${error.message || "Unknown error"}`,
      });
    }
  };

  const handleStatusChange = async (
    attachment: DocumentAttachment,
    newStatus: "upcoming" | "current" | "expired" | "disabled"
  ) => {
    try {
      // If setting to expired, automatically set expiration date to today
      const updateData: any = { status: newStatus };
      if (newStatus === "expired") {
        updateData.expires_at = new Date().toISOString();
      }

      await documentApi.updateAttachmentStatus(
        attachment.id,
        newStatus,
        updateData.expires_at
      );

      // Update local state
      setAttachments((prev) =>
        prev.map((a) => (a.id === attachment.id ? { ...a, ...updateData } : a))
      );

      toast({
        title: "Status updated",
        description:
          newStatus === "expired"
            ? `File marked as expired with expiration date set to today`
            : `File status changed to ${newStatus}`,
      });

      // Trigger parent refresh to update document status
      // This is especially important when marking files as expired, as it may change
      // the overall document status due to database triggers
      if (onSuccess && documentProp?.id) {
        onSuccess(documentProp.id);
      }
    } catch (error: any) {
      console.error("Error updating status:", error);
      
      const statusLabels = {
        current: "Current",
        upcoming: "Upcoming", 
        expired: "Expired",
        disabled: "Disabled"
      };
      
      toast({
        variant: "destructive",
        title: "Status update failed",
        description: `Failed to change file status to ${statusLabels[newStatus] || newStatus}. ${error.message || "Unknown error"}`,
      });
    }
  };

  const handleSetExpiration = async () => {
    if (!selectedAttachment || !expirationDate) return;

    try {
      await documentApi.setAttachmentExpiration(
        selectedAttachment.id,
        expirationDate
      );

      // Update local state
      setAttachments((prev) =>
        prev.map((a) =>
          a.id === selectedAttachment.id
            ? { ...a, expires_at: expirationDate }
            : a
        )
      );

      toast({
        title: "Expiration set",
        description: `File will expire on ${new Date(
          expirationDate
        ).toLocaleDateString()}`,
      });

      setExpirationDialogOpen(false);
      setSelectedAttachment(null);
      setExpirationDate("");
    } catch (error: any) {
      console.error("Error setting expiration:", error);
      toast({
        variant: "destructive",
        title: "Expiration update failed",
        description: error.message || "Failed to set expiration date.",
      });
    }
  };

  const openExpirationDialog = (attachment: DocumentAttachment) => {
    setSelectedAttachment(attachment);
    setExpirationDate(attachment.expires_at || "");
    setExpirationDialogOpen(true);
  };

  const handleSetEffectiveDate = async () => {
    if (!selectedAttachment || !effectiveDate) return;

    // Validate that the effective date is in the future
    const selectedDate = new Date(effectiveDate);
    const now = new Date();

    if (selectedDate <= now) {
      toast({
        variant: "destructive",
        title: "Invalid date",
        description: "Effective date must be in the future.",
      });
      return;
    }

    try {
      await documentApi.setAttachmentEffectiveDate(
        selectedAttachment.id,
        effectiveDate
      );

      // Update local state
      setAttachments((prev) =>
        prev.map((a) =>
          a.id === selectedAttachment.id
            ? { ...a, effective_date: effectiveDate }
            : a
        )
      );

      toast({
        title: "Effective date set",
        description: `File will become current on ${new Date(
          effectiveDate
        ).toLocaleDateString()}`,
      });

      setEffectiveDateDialogOpen(false);
      setSelectedAttachment(null);
      setEffectiveDate("");
    } catch (error: any) {
      console.error("Error setting effective date:", error);
      toast({
        variant: "destructive",
        title: "Effective date update failed",
        description: error.message || "Failed to set effective date.",
      });
    }
  };

  const openEffectiveDateDialog = (attachment: DocumentAttachment) => {
    setSelectedAttachment(attachment);
    setEffectiveDate(attachment.effective_date || "");
    setEffectiveDateDialogOpen(true);
  };





  const renderFileUploader = () => {
    return (
      <CompactFileUploader
        documentId={documentProp?.id}
        attachments={attachments}
        uploading={uploading}
        onFileUpload={handleFileUpload}
        onDeleteAttachment={handleDeleteAttachment}
        onStatusChange={handleStatusChange}
        onOpenExpirationDialog={openExpirationDialog}
        onOpenEffectiveDateDialog={openEffectiveDateDialog}
        acceptedFileTypes=".pdf,.docx,.doc,.txt,.csv"
        maxFileSize="10MB"
        disabled={!documentProp?.id}
      />
    );
  };

  const getSubmitButtonText = () => {
    if (submitButtonText) {
      return submitButtonText;
    }
    return documentProp ? "Update Document" : "Create Document";
  };

  const renderStatusIndicator = () => {
    if (!documentProp?.status) return null;

    const getStatusConfig = (status: string) => {
      switch (status) {
        case 'active':
          return {
            icon: FileCheck,
            label: 'Active',
            color: 'text-green-600',
          };
        case 'expired':
          return {
            icon: FileX,
            label: 'Expired',
            color: 'text-red-600',
          };
        case 'missing':
          return {
            icon: FileQuestion,
            label: 'Missing',
            color: 'text-orange-600',
          };
        default:
          return {
            icon: FileQuestion,
            label: 'Unknown',
            color: 'text-gray-600',
          };
      }
    };

    const statusConfig = getStatusConfig(documentProp.status);
    const StatusIcon = statusConfig.icon;

    return (
      <div className="flex items-center gap-2 mb-2">
        <StatusIcon className={`h-4 w-4 ${statusConfig.color}`} />
        <span className={`text-sm font-medium ${statusConfig.color}`}>
          Status: {statusConfig.label}
        </span>
      </div>
    );
  };

  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6"
      >
        <Card>
          <CardHeader>
            <CardTitle>Document Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Document information fields */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Document name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="document_type_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Document Type</FormLabel>
                  {/* Controlled on purpose: with defaultValue the display can
                      drift from the form state (8-Sep: a demo save silently
                      turned a questionnaire into a Buyer application). */}
                  <Select
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    value={field.value ? field.value.toString() : undefined}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select document type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {loadingTypes ? (
                        <div className="flex justify-center p-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      ) : (
                        safeDocumentTypes.map((type) => (
                          <SelectItem key={type.id} value={type.id.toString()}>
                            {type.name} (
                            {type.source === "file" ? "File" : "Platform"})
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {selectedDocumentType && (
                      <span className="text-sm">
                        Source:{" "}
                        <strong>
                          {selectedDocumentType.source === "file"
                            ? "File Upload"
                            : "Platform Form"}
                        </strong>
                      </span>
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Document Status Indicator */}
            {renderStatusIndicator()}

            <FormField
              control={form.control}
              name="publicly_available"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Publicly Available</FormLabel>
                    <FormDescription>
                      If checked, buyers can request this document
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {/* The document's own output template (8-Sep: "el output es parte
                del documento"). Empty = inherit from the document type. */}
            <FormField
              control={form.control}
              name="template_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Output template (override)</FormLabel>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value === "inherit" ? null : parseInt(value))
                    }
                    value={field.value ? field.value.toString() : "inherit"}
                    disabled={readOnly}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Inherit from document type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="inherit">
                        Inherit from document type
                      </SelectItem>
                      {compatibleTemplateFolders.map((template: any) => (
                        <SelectItem key={template.id} value={template.id.toString()}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    This document's generated output uses this template instead
                    of the type's. Leave on "Inherit" to keep today's behavior.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Per-document price overrides: the backend still honors them
                (documents PATCH `pricing_overrides` + the pricing engines),
                but the UI is parked — Mauro 8-Sep: the price does not belong
                on the document form; the future shape is document
                families/groups priced together. */}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Document Groups</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="document_groups"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Document Groups</FormLabel>
                    <FormDescription>
                      Enter a group name and press Enter to add it. Click a
                      group to remove it.
                    </FormDescription>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                        <Input
                          value={newGroupName}
                          onChange={(e) => setNewGroupName(e.target.value)}
                          placeholder="Add a group tag..."
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleCreateGroup}
                          disabled={!newGroupName.trim()}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add
                        </Button>
                      </div>

                      {filteredSuggestions.length > 0 && (
                        <div className="border rounded-md p-2 bg-background">
                          <p className="text-sm text-muted-foreground mb-2">
                            Suggestions:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {filteredSuggestions.map((group) => (
                              <Badge
                                key={group.id}
                                variant="outline"
                                className="cursor-pointer hover:bg-accent"
                                onClick={() => {
                                  addGroupToSelection(group.id);
                                  setNewGroupName("");
                                }}
                              >
                                {group.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {field.value && field.value.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {field.value.map((groupId, index) => {
                            const colorIndex = index % badgeColors.length;
                            return (
                              <Badge
                                key={groupId}
                                variant={badgeColors[colorIndex] as any}
                                className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() =>
                                  removeGroupFromSelection(groupId)
                                }
                              >
                                {groupsById[groupId] || `Group ${groupId}`}
                                <X className="h-3 w-3 ml-1" />
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Expiration Settings - Only for file-based document types */}
        {selectedDocumentType && selectedDocumentType.source === "file" && (
          <Card>
            <CardHeader>
              <CardTitle>Expiration Settings</CardTitle>
              <CardDescription>
                Configure document expiration overrides
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedDocumentType && (
                <div className="text-muted-foreground text-sm mb-2">
                  <strong>Current expiration (from document type):</strong>{" "}
                  {formatExpirationSettings(selectedDocumentType)}
                </div>
              )}
              {selectedDocumentType?.default_expiration_type === "days" && (
                <FormField
                  control={form.control}
                  name="expiration_override_days"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Override Expiration Time (days)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          placeholder="Leave empty to use document type default"
                          value={field.value === null ? "" : field.value}
                          onChange={(e) => {
                            const value =
                              e.target.value === ""
                                ? null
                                : parseInt(e.target.value);
                            field.onChange(value);
                            form.setValue(
                              "expiration_override_type",
                              value !== null ? "days" : undefined
                            );
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        Set expiration time in days (or leave empty for default)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              {selectedDocumentType?.default_expiration_type === "annual" && (
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="expiration_override_month"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Override Expiration Month</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={(val) => {
                              field.onChange(val ? parseInt(val) : null);
                              form.setValue(
                                "expiration_override_type",
                                val ? "annual" : undefined
                              );
                            }}
                            value={field.value ? String(field.value) : ""}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select month" />
                            </SelectTrigger>
                            <SelectContent>
                              {[
                                "January",
                                "February",
                                "March",
                                "April",
                                "May",
                                "June",
                                "July",
                                "August",
                                "September",
                                "October",
                                "November",
                                "December",
                              ].map((name, idx) => (
                                <SelectItem
                                  key={idx + 1}
                                  value={String(idx + 1)}
                                >
                                  {name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="expiration_override_day"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Override Expiration Day</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            max={31}
                            placeholder="Day"
                            value={field.value === null ? "" : field.value}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? null
                                  : parseInt(e.target.value);
                              field.onChange(value);
                              form.setValue(
                                "expiration_override_type",
                                value !== null ? "annual" : undefined
                              );
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
              {selectedDocumentType?.default_expiration_type === "date" && (
                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="expiration_override_year"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Override Expiration Year</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={2024}
                            placeholder="Year"
                            value={field.value === null ? "" : field.value}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? null
                                  : parseInt(e.target.value);
                              field.onChange(value);
                              form.setValue(
                                "expiration_override_type",
                                value !== null ? "date" : undefined
                              );
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="expiration_override_month"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Override Expiration Month</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={(val) => {
                              field.onChange(val ? parseInt(val) : null);
                              form.setValue(
                                "expiration_override_type",
                                val ? "date" : undefined
                              );
                            }}
                            value={field.value ? String(field.value) : ""}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select month" />
                            </SelectTrigger>
                            <SelectContent>
                              {[
                                "January",
                                "February",
                                "March",
                                "April",
                                "May",
                                "June",
                                "July",
                                "August",
                                "September",
                                "October",
                                "November",
                                "December",
                              ].map((name, idx) => (
                                <SelectItem
                                  key={idx + 1}
                                  value={String(idx + 1)}
                                >
                                  {name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="expiration_override_day"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Override Expiration Day</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            max={31}
                            placeholder="Day"
                            value={field.value === null ? "" : field.value}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? null
                                  : parseInt(e.target.value);
                              field.onChange(value);
                              form.setValue(
                                "expiration_override_type",
                                value !== null ? "date" : undefined
                              );
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Content</CardTitle>
          </CardHeader>
          <CardContent>
            {!documentProp?.id ? (
              <div className="flex flex-col items-center justify-center text-center py-6">
                <p className="text-muted-foreground">
                  Save the document first to access content configuration.
                </p>
              </div>
            ) : (
              <>
                {sourceType === "Platform" ? (
                  <div
                    className="space-y-6"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <p className="text-sm text-muted-foreground">
                      Build your form by adding steps, sections, and fields.
                      Each step can have multiple sections, and each section can
                      have multiple fields.
                    </p>
                    <FormBuilder
                      documentId={documentProp.id}
                      documentName={documentProp.name}
                      preventFormSubmission={
                        dialogFormSubmitDisabled || preventFormSubmission
                      }
                    />
                  </div>
                ) : (
                  renderFileUploader()
                )}
              </>
            )}
          </CardContent>
        </Card>
        {/* Document Form Builder - For document output format */}
        {documentProp?.id &&
          selectedDocumentType?.generates_output_document && (
            <DocumentFormBuilder
              documentId={documentProp.id}
              documentName={documentProp.name}
            />
          )}
        {!readOnly && !formId.includes("edit") && (
          <div className="flex justify-end space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate("/backoffice/documents")}
              type="button"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {getSubmitButtonText()}
            </Button>
          </div>
        )}
      </form>

      {/* Expiration Dialog */}
      <Dialog
        open={expirationDialogOpen}
        onOpenChange={setExpirationDialogOpen}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Set File Expiration</DialogTitle>
            <DialogDescription>
              Set when this file should expire. Expired files cannot be
              downloaded.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="expiration-date" className="text-right">
                Expires on
              </label>
              <Input
                id="expiration-date"
                type="datetime-local"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setExpirationDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleSetExpiration} disabled={!expirationDate}>
              Set Expiration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Effective Date Dialog */}
      <Dialog
        open={effectiveDateDialogOpen}
        onOpenChange={setEffectiveDateDialogOpen}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Set Effective Date</DialogTitle>
            <DialogDescription>
              Set when this file should become current. The file will
              automatically change from "upcoming" to "current" on this date.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="effective-date" className="text-right">
                Becomes current on
              </label>
              <Input
                id="effective-date"
                type="datetime-local"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEffectiveDateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleSetEffectiveDate} disabled={!effectiveDate}>
              Set Effective Date
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default DocumentForm;
